import axios from 'axios';

const DETAIL_KEYS = ['gate', 'expected', 'received', 'current_round', 'scope', 'open', 'limit'];

// Core answers with two envelopes: Boom-style { statusCode, error, message }
// and plain { error, fields }. Boom puts the human text in `message`.
function readMessage(data, status) {
    if (!data) return `Error ${status}`;
    if (typeof data === 'string') return data.slice(0, 300);
    if (typeof data.statusCode === 'number') return data.message || data.error || `Error ${status}`;
    return data.error || data.message || `Error ${status}`;
}

function readDetail(data) {
    if (!data || typeof data !== 'object') return null;
    const detail = {};
    for (const key of DETAIL_KEYS) if (key in data) detail[key] = data[key];
    return Object.keys(detail).length ? detail : null;
}

// Retry-After is not part of the Core contract; honour it only if a proxy sends it.
function readRetryAfterMs(headers) {
    const raw = headers?.['retry-after'];
    if (!raw) return null;
    const seconds = Number(raw);
    if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
    const date = Date.parse(raw);
    return Number.isFinite(date) ? Math.max(0, date - Date.now()) : null;
}

export class ApiError extends Error {
    constructor({ status, message, code, fields, detail, isNetwork, isTimeout, retryAfterMs, cause }) {
        super(message);
        this.name = 'ApiError';
        this.status = status ?? null;
        this.code = code ?? null;
        this.fields = fields ?? null;
        this.detail = detail ?? null;
        this.isNetwork = !!isNetwork;
        this.isTimeout = !!isTimeout;
        this.retryAfterMs = retryAfterMs ?? null;
        this.cause = cause;
    }

    get gate() { return this.detail?.gate ?? null; }
    get isClientError() { return this.status >= 400 && this.status < 500; }
    get isServerError() { return this.status >= 500; }
    get isUnauthorized() { return this.status === 401; }
    get isForbidden() { return this.status === 403; }
    get isNotFound() { return this.status === 404; }
    get isConflict() { return this.status === 409; }
    get isRateLimited() { return this.status === 429; }
    get isCancelled() { return this.code === 'CANCELLED'; }
    get isRetryable() { return this.isNetwork || this.isTimeout || this.isServerError; }

    static fromAxios(error) {
        if (error instanceof ApiError) return error;
        if (axios.isCancel(error)) {
            return new ApiError({ message: 'Solicitud cancelada', code: 'CANCELLED', cause: error });
        }
        if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
            return new ApiError({ message: 'Tiempo de espera agotado', isTimeout: true, cause: error });
        }
        if (!error?.response) {
            return new ApiError({ message: 'Error de red', isNetwork: true, cause: error });
        }
        const { status, data, headers } = error.response;
        return new ApiError({
            status,
            message: readMessage(data, status),
            fields: data?.fields || null,
            code: data?.code || null,
            detail: readDetail(data),
            retryAfterMs: readRetryAfterMs(headers),
            cause: error,
        });
    }
}

// Spanish copy for the statuses the UI must explain without domain context.
export function describeApiError(error) {
    if (!(error instanceof ApiError)) return 'Algo salió mal. Intenta de nuevo.';
    if (error.isNetwork) return 'Sin conexión con el servidor.';
    if (error.isTimeout) return 'El servidor tardó demasiado en responder.';
    if (error.isUnauthorized) return 'Tu sesión terminó. Vuelve a iniciar sesión.';
    if (error.isForbidden) return 'No tienes permiso para ver o hacer esto.';
    if (error.isNotFound) return 'No encontramos este elemento o no está en tu alcance.';
    if (error.isConflict) return 'Alguien cambió esto antes que tú. Recarga y vuelve a intentarlo.';
    if (error.isRateLimited) return 'Demasiadas solicitudes seguidas. Espera unos segundos.';
    if (error.isServerError) return 'Error del servidor. Intenta de nuevo.';
    return error.message || 'No se pudo completar la solicitud.';
}
